import React, { useState, useEffect, useRef } from 'react';
import { authAPI } from '../services/api';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import './Login.css';

function Login() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError]       = useState('');
  const [loading, setLoading]   = useState(false);

  // 2FA state
  const [otpStep, setOtpStep]       = useState(false);  // true = show OTP input
  const [otpToken, setOtpToken]     = useState('');
  const [phoneHint, setPhoneHint]   = useState('');
  const [otpCode, setOtpCode]       = useState('');
  const [resending, setResending]   = useState(false);
  const [resendMsg, setResendMsg]   = useState('');
  const otpRef = useRef(null);

  const navigate = useNavigate();
  const { login } = useAuth();

  const { user } = useAuth();
  useEffect(() => {
    if (user) navigate('/');
  }, [user, navigate]);

  useEffect(() => {
    if (otpStep && otpRef.current) otpRef.current.focus();
  }, [otpStep]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const response = await authAPI.login(username, password);
      const data = response.data;

      if (data.requires_otp) {
        // 2FA required — show OTP step
        setOtpToken(data.otp_token);
        setPhoneHint(data.phone_hint);
        setOtpStep(true);
      } else {
        // No 2FA — login directly
        _completeLogin(data);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid credentials. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const response = await authAPI.verifyOtp(otpToken, otpCode);
      _completeLogin(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Incorrect OTP. Please try again.');
      setOtpCode('');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setResending(true); setResendMsg('');
    try {
      await authAPI.resendOtp(otpToken);
      setResendMsg('OTP resent!');
      setTimeout(() => setResendMsg(''), 3000);
    } catch (e) {
      setResendMsg('Failed to resend');
    } finally {
      setResending(false);
    }
  };

  const _completeLogin = (data) => {
    login(data.access_token, {
      rbac_role:          data.rbac_role || 'member',
      is_prime_admin:     data.is_prime_admin || false,
      permissions:        data.permissions || [],
      cluster_access_ids: data.cluster_access_ids || [],
      server_access_ids:  data.server_access_ids || [],
      org_sections:       data.org_sections || [],
      org_section_actions: data.org_section_actions || {},
      effective_section_actions: data.effective_section_actions || {},
      organization_id:    data.organization_id || 1,
      organization_name:  data.organization_name || '',
      // Store credentials for silent token refresh (never sent to server directly)
      username,
      _pw: password,
    });
    navigate('/');
  };

  return (
    <div className="login-container">
      <div className="login-card">
        <div className="login-header">
          <h1>SarvKube</h1>
          <p>Kubernetes Cluster Management Platform</p>
        </div>

        {error && <div className="error-message">{error}</div>}

        {!otpStep ? (
          /* ── Step 1: Username + Password ── */
          <form onSubmit={handleSubmit} className="login-form">
            <div className="form-group">
              <label htmlFor="username">Username</label>
              <input id="username" type="text" placeholder="Enter your username"
                value={username} onChange={e => setUsername(e.target.value)} required />
            </div>
            <div className="form-group">
              <label htmlFor="password">Password</label>
              <input id="password" type="password" placeholder="Enter your password"
                value={password} onChange={e => setPassword(e.target.value)} required />
            </div>
            <button type="submit" className="login-button" disabled={loading}>
              {loading ? 'Logging in...' : 'Login'}
            </button>
          </form>
        ) : (
          /* ── Step 2: OTP Verification ── */
          <form onSubmit={handleVerifyOtp} className="login-form">
            <div style={{ textAlign: 'center', marginBottom: 20 }}>
              <div style={{ fontSize: 40, marginBottom: 8 }}>📱</div>
              <p style={{ margin: 0, fontSize: 14, color: 'var(--ink-2)' }}>
                OTP sent to <strong>{phoneHint}</strong>
              </p>
              <p style={{ margin: '4px 0 0', fontSize: 12, color: 'var(--muted)' }}>
                Enter the 6-digit code
              </p>
            </div>

            <div className="form-group">
              <label htmlFor="otp">OTP Code</label>
              <input
                ref={otpRef}
                id="otp"
                type="text"
                inputMode="numeric"
                placeholder="Enter 6-digit OTP"
                value={otpCode}
                onChange={e => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                maxLength={6}
                required
                style={{ textAlign: 'center', fontSize: 24, letterSpacing: 8 }}
              />
            </div>

            <button type="submit" className="login-button" disabled={loading || otpCode.length < 6}>
              {loading ? 'Verifying...' : 'Verify OTP'}
            </button>

            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 12, fontSize: 13 }}>
              <button type="button" onClick={() => { setOtpStep(false); setError(''); setOtpCode(''); }}
                style={{ background: 'none', border: 'none', color: 'var(--muted)', cursor: 'pointer', font: 'inherit' }}>
                ← Back to login
              </button>
              <button type="button" onClick={handleResend} disabled={resending}
                style={{ background: 'none', border: 'none', color: 'var(--brand)', cursor: 'pointer', fontWeight: 'var(--fw-semi)', font: 'inherit' }}>
                {resending ? 'Sending...' : 'Resend OTP'}
              </button>
            </div>
            {resendMsg && <div style={{ textAlign: 'center', fontSize: 12, color: 'var(--color-success-500)', marginTop: 6 }}>{resendMsg}</div>}
          </form>
        )}

        <div className="login-footer">
          Contact your administrator for credentials
        </div>
      </div>
    </div>
  );
}

export default Login;
